import moment from 'moment';
import type { Item } from 'shared-types';

import type { GolfOrderLine } from './orderLine';

export const GOLF_ITEM_TYPE = 'golf';

type GolfPlayerIds = {
  firstName?: string;
  lastName?: string;
};

type GolfHireLineIds = {
  id?: string | number;
  name?: string;
  quantity?: number;
  unitPrice?: number;
  total?: number;
};

type GolfLineIds = {
  courseId?: string;
  startTime?: string;
  bookingTypeId?: string;
  resourceId?: string;
  players?: unknown;
  playerCount?: unknown;
  hireItems?: unknown;
  golfReference?: string;
  golfReservationId?: string;
  cancellationDeadline?: string | null;
};

type GolfLineSource = {
  checkIn?: string | null;
  status?: string | null;
  cancellationDetails?: { isCancellable?: boolean | null } | null;
  bookable?: { ids?: unknown } | null;
};

export type GolfHireLine = {
  id: string;
  name: string;
  quantity?: number;
};

export const isGolfItem = (item: { type?: string | null }): boolean => item.type === GOLF_ITEM_TYPE;

export const hasGolfItem = (items: Array<{ type?: string | null }> | null | undefined): boolean =>
  (Array.isArray(items) ? items : []).some(isGolfItem);

const golfLineIds = (item: GolfLineSource): GolfLineIds =>
  (item.bookable?.ids ?? {}) as GolfLineIds;

const golfPlayers = (item: GolfLineSource): GolfPlayerIds[] => {
  const { players } = golfLineIds(item);

  return Array.isArray(players) ? players : [];
};

export const golfCourseId = (item: Item): string | undefined => {
  const { courseId } = golfLineIds(item);

  return courseId ? String(courseId) : undefined;
};

export const golfPlayerNames = (item: GolfLineSource): string[] =>
  golfPlayers(item)
    .map((player) => `${player.firstName ?? ''} ${player.lastName ?? ''}`.trim())
    .filter((name) => name.length > 0);

export const golfPlayerCount = (item: GolfLineSource): number => {
  const players = golfPlayers(item);
  if (players.length > 0) return players.length;

  const { playerCount } = golfLineIds(item);

  return typeof playerCount === 'number' ? playerCount : 0;
};

export const golfTeeTime = (item: GolfLineSource): moment.Moment | undefined => {
  const { startTime } = golfLineIds(item);
  const fromStartTime =
    typeof startTime === 'string' && startTime !== '' ? moment.parseZone(startTime) : undefined;
  if (fromStartTime?.isValid()) return fromStartTime;

  const fromCheckIn = item.checkIn ? moment(item.checkIn) : undefined;

  return fromCheckIn?.isValid() ? fromCheckIn : undefined;
};

export const golfTeeTimeWallClock = (item: GolfLineSource): Date | undefined => {
  const teeTime = golfTeeTime(item);

  return teeTime
    ? new Date(
        Date.UTC(
          teeTime.year(),
          teeTime.month(),
          teeTime.date(),
          teeTime.hours(),
          teeTime.minutes()
        )
      )
    : undefined;
};

export const golfHireLines = (item: GolfLineSource): GolfHireLine[] => {
  const { hireItems } = golfLineIds(item);

  return (Array.isArray(hireItems) ? (hireItems as GolfHireLineIds[]) : []).flatMap((hire) => {
    const name = typeof hire?.name === 'string' ? hire.name.trim() : '';
    if (hire?.id == null || !name) return [];

    return [
      {
        id: String(hire.id),
        name,
        quantity: typeof hire.quantity === 'number' ? hire.quantity : undefined,
      },
    ];
  });
};

export const golfReference = (item: GolfLineSource): string | undefined =>
  golfLineIds(item).golfReference || undefined;

export const golfOrderLine = (item: GolfLineSource): GolfOrderLine => {
  const ids = golfLineIds(item);

  return {
    startTime: ids.startTime ?? item.checkIn,
    playerCount: golfPlayerCount(item),
    reference: golfReference(item),
    status: item.status,
    isCancellable: item.cancellationDetails?.isCancellable,
    cancellationDeadline: ids.cancellationDeadline,
    hireLines: golfHireLines(item),
  };
};

export const golfPerPlayerMinorUnits = (item: Item): number | undefined => {
  const [perPlayer] = item.bookable?.pricesPerNight ?? [];

  return typeof perPlayer === 'number' && perPlayer > 0 ? perPlayer : undefined;
};
